import { useCallback, useEffect, useRef, useState } from "react";
import { friendliesService } from "../../services/friendliesApi";

// TP-Friendlies browse feed state. Built on the useClubNetworkFeed / useLastMinute
// Feed shape: a URLSearchParams builder over the server's supported filters, an
// AbortController per fetch, and an optimistic Saved toggle.
//
// Unlike Club-Network there is NO batch side-channel: the feed card already carries
// `saved` and `my_application` INLINE (the viewset computes them per-viewer). The
// save endpoint is an idempotent toggle returning the new `{saved}` state, so the
// optimistic flip reconciles to the server's answer.
//
// Server filters (friendly_views._apply_filters + fr-be-builder 11de66d): team_category,
// date_from, date_to, country, region, tier, q. `tier` is the full ?tier= vocabulary
// ("1" | "1,2" | "3_or_lower" | "any"); `q` is free-text over club name / context /
// team_label / camp_region.
const DEFAULT_FILTERS = {
  team_category: "",
  country: "",
  region: "",
  tier: "", // "1" | "1,2" | "3_or_lower" | "" → BE ?tier= (see TierSelector serialize)
  date_from: "",
  date_to: "",
  q: "",
};

const ACTIVE_KEYS = ["team_category", "country", "region", "tier", "date_from", "date_to", "q"];

export const useFriendliesFeed = () => {
  const [requests, setRequests] = useState([]);
  const [count, setCount] = useState(0);
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [savedOnly, setSavedOnly] = useState(false);
  const abortRef = useRef(null);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    ACTIVE_KEYS.forEach((k) => {
      if (filters[k]) p.append(k, filters[k]);
    });
    if (savedOnly) p.append("saved_only", "true");
    return p;
  }, [filters, savedOnly]);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await friendliesService.getFeed(buildParams(), controller.signal);
      if (res.success) {
        setRequests(res.data);
        setCount(res.count);
        // Keep the strip aggregate stable across the Saved view (it's the full-feed
        // bucketing); only refresh it when the server actually returns buckets.
        if (res.buckets && res.buckets.length) setBuckets(res.buckets);
      } else {
        setError(res.error || "Failed to load friendlies.");
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.name === "AbortError") return;
      setError("Failed to load friendlies. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchFeed();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchFeed]);

  // Optimistic Saved toggle. The endpoint is idempotent and returns the new state,
  // so we flip immediately, then reconcile to the server's answer (revert on fail).
  const toggleSaved = useCallback(async (request) => {
    const was = !!request.saved;
    setRequests((prev) => prev.map((r) => (r.id === request.id ? { ...r, saved: !was } : r)));
    const res = await friendliesService.toggleSave(request.id);
    if (!res.success) {
      setRequests((prev) => prev.map((r) => (r.id === request.id ? { ...r, saved: was } : r)));
      return;
    }
    const serverSaved = res.data?.saved;
    if (typeof serverSaved === "boolean") {
      setRequests((prev) =>
        prev.map((r) => (r.id === request.id ? { ...r, saved: serverSaved } : r)),
      );
      // In the Saved-only view an un-save drops the card from the list.
      if (savedOnly && serverSaved === false) {
        setRequests((prev) => prev.filter((r) => r.id !== request.id));
      }
    }
  }, [savedOnly]);

  // After a successful apply, flip the card to the quiet "Applied" state in place.
  const markApplied = useCallback((requestId) => {
    setRequests((prev) =>
      prev.map((r) => (r.id === requestId ? { ...r, my_application: { state: "applied" } } : r)),
    );
  }, []);

  const setFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const setDateRange = (from, to) =>
    setFilters((prev) => ({ ...prev, date_from: from || "", date_to: to || "" }));

  const removeFilter = (key) => {
    if (key === "date") return setFilters((prev) => ({ ...prev, date_from: "", date_to: "" }));
    return setFilters((prev) => ({ ...prev, [key]: "" }));
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSavedOnly(false);
  };

  const toggleSavedOnly = () => setSavedOnly((v) => !v);

  const hasActiveFilters =
    ACTIVE_KEYS.some((k) => !!filters[k]) || savedOnly;

  return {
    requests,
    count,
    buckets,
    loading,
    error,
    filters,
    savedOnly,
    setFilter,
    setDateRange,
    removeFilter,
    clearFilters,
    toggleSavedOnly,
    toggleSaved,
    markApplied,
    hasActiveFilters,
    refetch: fetchFeed,
  };
};

export default useFriendliesFeed;
